/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import _ from 'lodash';
import screenfullDefault, { Screenfull } from 'screenfull';
import { getAdMethods } from 'utils/adMethods';
import { SettingSectionName } from '../components/newSkin/types';
import { translateKeysCMS } from '../translates/translateKeysCMS';
import { dispatchEtUiEvent } from '../utils';
// import { isAndroid, isMobile } from './isMobile';

const screenfull = screenfullDefault as Screenfull;

export const initPlayerMethods = (
  player: Player,
  getState: () => PlayerState
) => {
  if (screenfull.enabled) {
    screenfull.on('change', () => {
      player.emit('skin:fullscreenStateChanged', screenfull.isFullscreen);
    });
  }

  const self = {
    getHybridLinearAdShowed: () => {
      const { adLinear, adPlaying, isHybridAdSkin } = getState();
      return adLinear && adPlaying && isHybridAdSkin;
    },
    play: (isEmitSkinClick = true) => {
      if (self.getHybridLinearAdShowed()) {
        return getAdMethods(player).adResume();
      }
      if (isEmitSkinClick) {
        player.emit('skin:playClick');
      }
      player.play();
    },
    pause: (isEmitSkinClick = true) => {
      if (self.getHybridLinearAdShowed()) {
        return getAdMethods(player).adPause();
      }
      if (isEmitSkinClick) {
        player.emit('skin:pauseClick');
      }
      player.pause();
    },
    seek: (
      time: number,
      silent: boolean = false
      // channelState: PlayerState['channelState'],
      // type: PlayerState['type'],
      // contentType: PlayerState['contentType'],
      // channelType: string
    ) => {
      const { channelState, type, contentType, channel } = getState();

      const channelType = channel?.type;

      if (!silent) player.emit('skin:seekClick');
      if (channelType === 'dvrChannel') {
        // mggp-plugin-dvr fully processes seek
        return player.emit('dvrChannel:seek', time);
      } else if (channelState === 'onAir' || channelState === 'timeshift') {
        // this 'if' for mggp-plugin-vod-channels, it processes only onAir and timeshift states
        // for 'catchup' state need to use player.seekTo
        if (contentType === 'm_ch_vod') {
          return player.emit('vodChannel:seek', time);
        }
      } else if (type === 'live_dvr') {
        // mggp-plugin-live-dvr
        return player.emit('liveDvr:seek', time);
      } else {
        // vod seek
        return player.seekTo(time);
      }
    },
    mute: () => {
      if (self.getHybridLinearAdShowed()) {
        getAdMethods(player).adMute();
      } else {
        player.mute();
      }
    },
    unMute: () => {
      if (self.getHybridLinearAdShowed()) {
        getAdMethods(player).adUnMute();
      } else {
        player.unMute();
      }
    },
    setVolume: (volume: number, startSlideVolume?: number) => {
      const isHybridLinearAdShowed = self.getHybridLinearAdShowed();
      const adMethonds = isHybridLinearAdShowed
        ? getAdMethods(player)
        : undefined;

      if (volume && player.get('muted')) {
        player.unMute();
        adMethonds?.adUnMute();
      }

      player.setVolume(volume);
      adMethonds?.adSetVolume(volume);

      if (volume === 0.1) {
        // stange old condition, I don't want to touch it
        player.mute();
        adMethonds?.adMute();
      } else if (volume === 0 && startSlideVolume) {
        // startSlidingVolume is checked on undefined and 0
        player.mute();
        adMethonds?.adMute();
        player.setVolume(startSlideVolume);
        adMethonds?.adSetVolume(startSlideVolume);
      }
    },
    setAudioTrack: (trackId: number) => {
      player.emit('skin:audioButtonClick', trackId);
      player.setAudioTrack(trackId);
    },
    setQuality: (bitrate: QualityListItem['bitrate']) =>
      player.setQuality(bitrate),
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    setSubtitles: (langTag: 'off' | string) => player.setSubtitles(langTag),
    toggleFavorite: (currentState: boolean) => {
      const { videoId: id } = getState();
      player.emit('player:favoriteButtonClick', { state: currentState, id });
    },
    nextChannel: () => player.emit('skin:nextChannelClick'),
    prevChannel: () => player.emit('skin:prevChannelClick'),
    nextProgram: (
      channel: TVChannel,
      program: TVProgram,
      type: PlayerState['type'],
      contentType: PlayerState['contentType']
    ) => {
      player.emit('skin:nextProgramClick');
      const nextProgram = channel.getNextProgram(program);
      if (type === 'tv' && contentType !== 'm_ch_vod') {
        if (nextProgram.state === 1)
          return player.emit('dvrChannel:backOnAir', { startFromZero: true });
        return player.emit('dvrChannel:playProgram', nextProgram);
      }
      return player.emit('vodChannel:playProgram', nextProgram);
    },
    prevProgram: (
      channel: TVChannel,
      program: TVProgram,
      type: PlayerState['type'],
      contentType: PlayerState['contentType']
    ) => {
      player.emit('skin:prevProgramClick');
      const prevProgram = channel.getPrevProgram(program);
      if (type === 'tv' && contentType !== 'm_ch_vod') {
        return player.emit('dvrChannel:playProgram', prevProgram);
      }
      return player.emit('vodChannel:playProgram', prevProgram);
    },
    nextEpisode: (next: PrevNextStreamObject) => {
      player.emit('skin:nextClick');
      player.emit('changeVideo', next.id);
    },
    prevEpisode: (prev: PrevNextStreamObject) => {
      player.emit('skin:prevClick');
      player.emit('changeVideo', prev.id);
    },
    skipIntro: (skipIntroText: string) => {
      player.emit('sip:skip', { text: skipIntroText });
      dispatchEtUiEvent(
        {
          text: skipIntroText,
          code: 'intro_button',
          type: 'button',
        },
        player
      );
    },
    onSkipIntroEnded: () => {},
    backOnAir: () => {
      const { contentType } = getState();

      if (
        contentType === 'tv_dvr' ||
        contentType === 'tv_dvr_vod' ||
        contentType === 'tv_dvr_catchup'
      ) {
        return player.emit('dvrChannel:backOnAir');
      }
      return player.emit('vodChannel:backOnAir');
    },
    setSkipAdProperty: (value: boolean) => player.set('ad:skipAd', value),
    enterFullscreen: () => {
      self.exitPiP();

      const playerTarget = document.getElementById('playerContainer')!;
      if (screenfull.enabled) {
        void screenfull.request(playerTarget);
      } else {
        const video = document.querySelector('video[class="player:video"]');
        if (
          video instanceof HTMLVideoElement &&
          _.isFunction(video.webkitEnterFullscreen)
        ) {
          video.webkitEnterFullscreen();
        }
      }
    },
    exitFullscreen: () => {
      if (screenfull.enabled) {
        void screenfull.exit();
      } else {
        const video = document.querySelector('video[class="player:video"]');
        if (
          video instanceof HTMLVideoElement &&
          _.isFunction(video.webkitExitFullscreen)
        ) {
          video.webkitExitFullscreen();
        }
      }
    },
    translate: (id: string, params?: Record<string, string>) => {
      const i18n = player.config.i18n;
      let result;
      if (id === 'skip_ads' && params) {
        result = `${i18n(id)} ${i18n('through')} ${params.sec} ${i18n('second')}`;
      } else if (id === 'advertisement_additional_part' && params) {
        result = (i18n(id) || id)
          .replace('%num%', params.number)
          .replace('%total%', params.count);
      } else if (id === 'ad_will_start_after' && params) {
        result = (i18n(id) || id).replace('{sec}', params.sec);
      } else {
        result = i18n(id) || id;
      }
      return result;
    },
    retry: () => {
      const opts = player.config.checkDefaultOptions();
      const stream = player.utils.merge({}, player.get('videoInfo'));
      const src: string = stream ? stream.src : '';
      player.stop();
      player.load(src, stream, opts);
    },
    setMessageContainer: (container: HTMLElement) => {
      const prevContainer = player.get('messageContainer');
      if (prevContainer === null) {
        player.registerProperty('messageContainer', container, true);
      } else {
        player.set('messageContainer', container || undefined);
      }
    },
    emitChromecastAction: (action: 'needToConnect' | 'needToDisonnect') => {
      player.emit('chromecast', action);

      if (action === 'needToConnect') {
        dispatchEtUiEvent(
          {
            code: 'to_chromecast_mode',
            type: 'button',
          },
          player
        );
      }
    },
    setAutoplay: (state: boolean) => player.set('autoplay', state),
    monthToString: (month: number) => player.config.i18n(month) || '',
    getCurrentTimestamp: () => player.get('epg:currentTimestamp'),
    onBitrateBySubscriptionButtonClick: () =>
      player.emit('skin:subscriptionQualityButtonClick'),
    onCloseClick: () => player.emit('skin:closeButtonClick'),
    onLiveDvrBackOnAir: () => player.emit('liveDvr:backOnAir'),
    onStartOver: () => {
      player.emit('skin:startOver');
      self.seek(0, true);
    },
    emitSkinVisibilityBeginChange: (state: boolean) =>
      player.emit('skin:skinVisibilityBeginChange', state),
    emitSafeZoneSizeChanged: (
      zone: Array<{
        zone_id: string;
        top: number;
        right: number;
        bottom: number;
        left: number;
      }>
    ) => {
      player.set('interactive:zone', zone);
    },
    emitInteractiveAction(action: Record<string, any>) {
      player.emit('interactive:elementAction', action);
    },
    onSettingsCategoryOpened: (name: SettingSectionName) => {
      dispatchEtUiEvent({ code: `settings_${name}`, type: 'button' }, player);
    },
    onClickRemindIcon: () => player.emit('player:remindButtonClick'),
    onClickPipButton: () => player.emit('pip:pipButtonClick'),
    onSharePopupOpen: (callback: () => void) =>
      player.on('skin:openSharePopup', callback),
    onClickShareButton: () => {
      player.emit('skin:shareButtonClick');
    },
    onClickVolumeButton: () => {
      player.emit('skin:volumeButtonClick');
    },
    onShareWidgetCopyButtonClick: () => {
      dispatchEtUiEvent(
        {
          text: self.translate(translateKeysCMS.copyTextButton),
          code: 'copy_share_link',
          type: 'button',
        },
        player
      );
    },
    onShareWidgetShowed: () => {
      dispatchEtUiEvent(
        {
          text: self.translate(translateKeysCMS.shareButton),
          code: 'share',
          type: 'button',
        },
        player
      );
    },
    onShareWidgetTimestampOn: () => {
      dispatchEtUiEvent(
        {
          text: self.translate(translateKeysCMS.shareFromSocialTextByTime),
          code: 'share_timestamp',
          type: 'button',
        },
        player
      );
    },
    setInteractiveTimeData: (position: number) => {
      player.set('interactive:position', position);
    },
    registerRemoteEvent: (name: string) => {
      player.emit('register_remote_event', name);
    },
    unRegisterRemoteEvent: (name: string) => {
      player.emit('un_register_remote_event', name);
    },
    onOpenEpg: () => {
      player.emit('epg2:epgButtonClick');
    },
    onCloseEpg: () => {
      player.emit('epg2:smallModePlayerClick');
    },
    exitPiP: () => {
      if (document.pictureInPictureElement) {
        void document.exitPictureInPicture();
      } else {
        const videoPlayerInstance = getState().videoPlayerInstance;
        if (
          videoPlayerInstance?.webkitPresentationMode === 'picture-in-picture'
        ) {
          videoPlayerInstance.webkitSetPresentationMode?.('inline');
        }
      }
    },
  };

  return self;
};

export type PlayerMethodsType = ReturnType<typeof initPlayerMethods>;
